import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RadioTower } from 'lucide-react'
import type { ServerHook } from '@apiary/shared'
import type { CreateServerRequest, ServerDto, ServerProbeDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { equalDeep, useDraft } from '@/ui/hooks/useDraft'
import { Button } from '@/ui/primitives/Button'
import { Dialog } from '@/ui/primitives/Dialog'
import { NumberField, TextField } from '@/ui/primitives/Field'
import { Select } from '@/ui/primitives/Select'
import { fieldErrorsOf, problemText } from '../shared/problem'
import { HookList, hookProblem } from './HookEditor'
import { ProbeBadge } from './ProbeBadge'

/**
 * Диалог профиля сервера: одна форма на создание и на правку.
 *
 * Поля совпадают полностью, поэтому две копии разошлись бы при первой же новой
 * настройке. `server === null` означает «создаём».
 *
 * Проба живёт прямо в форме и работает по введённым host:port, а не по
 * сохранённому профилю: узнать «а этот адрес вообще отвечает и поддержан ли
 * ядром» нужно ДО сохранения, иначе профиль создаётся вслепую.
 *
 * Поля держит `useDraft`: список серверов перечитывается после каждой мутации
 * (в том числе чужой — карточку бота инвалидирует WS), и синхронизация формы
 * через `useEffect` по объекту профиля затирала бы набранное имя или адрес
 * прямо во время ввода.
 */

export interface ServerDialogProps {
  open: boolean
  server: ServerDto | null
  onClose: () => void
}

interface FormState extends CreateServerRequest {
  hooks: ServerHook[]
}

function emptyForm(): FormState {
  return {
    name: '',
    host: '',
    port: 25565,
    versionMode: 'auto',
    version: null,
    joinCommand: null,
    hooks: []
  }
}

function formOf(server: ServerDto): FormState {
  return {
    name: server.name,
    host: server.host,
    port: server.port,
    versionMode: server.versionMode,
    version: server.version,
    joinCommand: server.joinCommand,
    hooks: server.hooks
  }
}

/** Форма → тело запроса. Нормализация здесь, а не по каждому нажатию клавиши. */
function dtoOf(state: FormState): CreateServerRequest {
  return {
    name: state.name.trim(),
    host: state.host.trim(),
    port: state.port,
    versionMode: state.versionMode,
    version: state.versionMode === 'manual' ? (state.version ?? '').trim() : null,
    joinCommand: (state.joinCommand ?? '').trim() === '' ? null : (state.joinCommand ?? '').trim(),
    hooks: state.hooks
  }
}

/**
 * Только изменённые поля (docs/10 §3.4a). Сервер пишет в аудит список того, что
 * поменялось, и «сменил порт на тот же самый» там — ложь; пустой diff вообще не
 * повод ходить по сети.
 *
 * Сравниваются НОРМАЛИЗОВАННЫЕ значения с обеих сторон: иначе лишний пробел,
 * который `dtoOf` всё равно срежет, выглядел бы как правка.
 */
function changedFieldsOf(
  next: CreateServerRequest,
  previous: CreateServerRequest
): Partial<CreateServerRequest> {
  return {
    ...(next.name === previous.name ? {} : { name: next.name }),
    ...(next.host === previous.host ? {} : { host: next.host }),
    ...(next.port === previous.port ? {} : { port: next.port }),
    ...(next.versionMode === previous.versionMode ? {} : { versionMode: next.versionMode }),
    ...(next.version === previous.version ? {} : { version: next.version }),
    ...(next.joinCommand === previous.joinCommand ? {} : { joinCommand: next.joinCommand }),
    ...(equalDeep(next.hooks, previous.hooks) ? {} : { hooks: next.hooks })
  }
}

export function ServerDialog({ open, server, onClose }: ServerDialogProps): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)

  const seed = open ? (server?.id ?? 'new') : null
  const draft = useDraft(server === null ? emptyForm() : formOf(server), seed)
  const form = draft.value
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [preview, setPreview] = useState<ServerProbeDto | null>(null)

  // Сброс сопутствующего состояния — по метке формы, а не по объекту профиля:
  // иначе фоновый рефетч гасил бы результат только что сделанной пробы.
  useEffect(() => {
    setErrors({})
    setFormError(null)
    setPreview(null)
  }, [seed])

  const patch = (next: Partial<FormState>): void => draft.patch(next)

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    if (form.name.trim() === '') found.name = 'Название нужно, чтобы отличать профили в списках'
    if (form.host.trim() === '') found.host = 'Адрес обязателен'
    else if (/\s/.test(form.host.trim())) found.host = 'В адресе не бывает пробелов'
    if (!Number.isInteger(form.port) || form.port < 1 || form.port > 65535) {
      found.port = 'Порт — целое число от 1 до 65535'
    }
    if (form.versionMode === 'manual' && (form.version ?? '').trim() === '') {
      found.version = 'Укажите версию или переключитесь на автоопределение'
    }
    const broken = form.hooks
      .map((hook, index) => {
        const problem = hookProblem(hook)
        return problem === null
          ? null
          : `${hook.name.trim() === '' ? `хук ${index + 1}` : hook.name}: ${problem}`
      })
      .filter((item): item is string => item !== null)
    setErrors(found)
    setFormError(broken.length === 0 ? null : `Не сохранено — ${broken.join('; ')}`)
    return Object.keys(found).length === 0 && broken.length === 0
  }

  const onFailure = (error: unknown): void => {
    const fields = fieldErrorsOf(error)
    if (Object.keys(fields).length > 0) setErrors(fields)
    setFormError(problemText(error))
  }

  const create = useMutation({
    mutationFn: (dto: CreateServerRequest) => api.servers.create(dto),
    onSuccess: created => {
      void queryClient.invalidateQueries({ queryKey: qk.servers.list })
      push({
        message: `Профиль «${created.name}» создан`,
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      onClose()
    },
    onError: onFailure
  })

  const save = useMutation({
    mutationFn: (dto: Partial<CreateServerRequest>) => {
      if (server === null) throw new Error('нет профиля для правки')
      return api.servers.update(server.id, dto)
    },
    onSuccess: updated => {
      void queryClient.invalidateQueries({ queryKey: qk.servers.list })
      // Карточка бота показывает адрес сервера — иначе там останется старый.
      void queryClient.invalidateQueries({ queryKey: qk.bots.root })
      push({
        message: `Профиль «${updated.name}» сохранён`,
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      onClose()
    },
    onError: onFailure
  })

  const probe = useMutation({
    mutationFn: () => api.servers.probe({ host: form.host.trim(), port: form.port }),
    onSuccess: result => setPreview(result),
    onError: onFailure
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={server === null ? 'Новый профиль сервера' : `Профиль «${server.name}»`}
      note="Профиль описывает, куда и как заходит бот: адрес, версия, команда после входа и хуки"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="secondary"
            loading={probe.isPending}
            disabled={form.host.trim() === ''}
            onClick={() => probe.mutate()}
          >
            <RadioTower aria-hidden className="size-4" />
            Пропинговать
          </Button>
          <Button
            variant="primary"
            loading={create.isPending || save.isPending}
            onClick={() => {
              if (!validate()) return
              if (server === null) {
                create.mutate(dtoOf(form))
                return
              }
              const dto = changedFieldsOf(dtoOf(form), dtoOf(formOf(server)))
              // Пустой diff закрывает диалог без запроса (docs/10 §3.4a).
              if (Object.keys(dto).length === 0) {
                onClose()
                return
              }
              save.mutate(dto)
            }}
          >
            {server === null ? 'Создать' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <TextField
          label="Название профиля"
          value={form.name}
          required
          autoFocus
          error={errors.name ?? null}
          onBlur={() => validate()}
          onChange={event => patch({ name: event.target.value })}
        />

        <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
          <TextField
            label="Адрес"
            mono
            placeholder="mc.example.org"
            value={form.host}
            required
            error={errors.host ?? null}
            onBlur={() => validate()}
            onChange={event => patch({ host: event.target.value })}
          />
          <NumberField
            label="Порт"
            value={form.port}
            min={1}
            max={65535}
            error={errors.port ?? null}
            onBlur={() => validate()}
            onChange={event => patch({ port: Number(event.target.value) })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Версия Minecraft"
            value={form.versionMode}
            onValueChange={value => patch({ versionMode: value === 'manual' ? 'manual' : 'auto' })}
            options={[
              { value: 'auto', label: 'Определять по пингу', note: 'auto' },
              { value: 'manual', label: 'Задать вручную', note: 'manual' }
            ]}
            hint={
              form.versionMode === 'auto'
                ? 'Версию возьмём из ответа сервера при подключении'
                : 'Нужно, когда сервер врёт о версии в пинге'
            }
          />
          {form.versionMode === 'manual' ? (
            <TextField
              label="Версия"
              mono
              placeholder="26.2"
              value={form.version ?? ''}
              error={errors.version ?? null}
              onBlur={() => validate()}
              onChange={event => patch({ version: event.target.value })}
            />
          ) : null}
        </div>

        <TextField
          label="Команда после входа"
          mono
          hint="Выполнится один раз после появления в мире: например, /survival"
          value={form.joinCommand ?? ''}
          onChange={event => patch({ joinCommand: event.target.value })}
        />

        <section className="flex flex-col gap-3 border-t border-line pt-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-display text-base font-semibold text-ink">Хуки сервера</h3>
            <span className="text-2xs text-ink-muted">
              реакции на строки чата: рестарт, кик, приветствие
            </span>
          </div>
          <HookList hooks={form.hooks} onChange={hooks => patch({ hooks })} />
        </section>

        {preview === null ? null : (
          <div className="flex flex-col gap-2 rounded-sm border border-line bg-panel-2 p-3">
            <p className="text-2xs text-ink-muted">Ответ сервера прямо сейчас</p>
            <ProbeBadge probe={preview} showTime={false} />
            {preview.motd === null ? null : (
              <p className="truncate font-mono text-2xs text-ink-muted">{preview.motd}</p>
            )}
          </div>
        )}

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}
      </div>
    </Dialog>
  )
}
